import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { TRACKER_REPORT_ROWS } from "@sd/lib/trackerReportConstants";
import { TRACKER_ROW_CHART_CONFIG } from "@sd/lib/trackerChartTheme";
import type { TrackerStatusChartRow } from "@sd/lib/trackerReportApi";
import { useIsMobile } from "@sd/hooks/use-mobile";

const chartConfig = {
  bug: TRACKER_ROW_CHART_CONFIG.bug,
  new_requirement: TRACKER_ROW_CHART_CONFIG.new_requirement,
  suggestion: TRACKER_ROW_CHART_CONFIG.suggestion,
} satisfies ChartConfig;

const STACK_KEYS = ["bug", "new_requirement", "suggestion"] as const;

interface TrackerStatusChartProps {
  data: TrackerStatusChartRow[];
}

export function TrackerStatusChart({ data }: TrackerStatusChartProps) {
  const isMobile = useIsMobile();
  const chartData = useMemo(() => [...data], [data]);

  const chartHeight = Math.max(isMobile ? 200 : 220, chartData.length * (isMobile ? 28 : 32) + (isMobile ? 64 : 72));
  const yAxisWidth = isMobile ? 96 : 132;

  if (chartData.length === 0) {
    return (
      <div className="flex min-h-[180px] items-center justify-center overflow-hidden rounded-xl border border-border bg-card px-4 py-10 text-sm text-muted-foreground shadow-sm">
        No status data to chart yet.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="border-b border-border px-4 py-3">
        <h2 className="text-center text-sm font-semibold text-foreground">
          Issues by Status
        </h2>
      </div>
      <div className="overflow-x-auto px-2 py-4 sm:px-4">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto w-full min-w-[280px]"
          style={{ height: chartHeight }}
        >
          <BarChart
            accessibilityLayer
            data={chartData}
            layout="vertical"
            margin={{ top: 4, right: isMobile ? 8 : 16, left: 0, bottom: 4 }}
          >
            <CartesianGrid horizontal={false} strokeDasharray="3 3" />
            <XAxis
              type="number"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              allowDecimals={false}
              tick={{ fontSize: isMobile ? 10 : 12 }}
            />
            <YAxis
              type="category"
              dataKey="statusLabel"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={yAxisWidth}
              tick={{ fontSize: isMobile ? 10 : 12 }}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend content={<ChartLegendContent />} />
            {STACK_KEYS.map(key => (
              <Bar
                key={key}
                dataKey={key}
                stackId="status"
                fill={`var(--color-${key})`}
                stroke="hsl(var(--background))"
                strokeWidth={1}
                radius={[0, 0, 0, 0]}
              />
            ))}
          </BarChart>
        </ChartContainer>
      </div>
      <div className="border-t border-border px-3 py-2 text-center text-[11px] text-muted-foreground sm:px-4 sm:text-xs">
        {TRACKER_REPORT_ROWS.map(row => row.label).join(" · ")}
      </div>
    </div>
  );
}
